/**
 * Supabase Service Agreements API Service
 *
 * Handles all service agreement operations.
 */

import { supabase } from './client';
import type { ServiceAgreement, ServiceAgreementInsert, ServiceAgreementUpdate, Json } from './database.types';

// Agreement status type
export type AgreementStatus = 'draft' | 'sent' | 'signed' | 'completed';

// Agreement line item type
export interface AgreementLineItem {
  id: string;
  sku: string;
  description: string;
  qty: string;
  price: string;
  lineTotal: string;
}

// Frontend Agreement type (camelCase)
export interface AgreementData {
  id: string;
  createdAt: string;
  updatedAt: string;
  leadId: string | null;
  // Company Info
  companyName: string;
  businessAddress: string | null;
  ownerName: string | null;
  state: string;
  cityAndState: string | null;
  // Agreement Details
  agreementDate: string;
  salesOrder: string | null;
  // Customer Info
  customerName: string;
  customerAddress: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  // Job Site
  jobSiteAddress: string | null;
  jobSiteDetails: string | null;
  // Line Items & Pricing
  lineItems: AgreementLineItem[];
  subtotal: number;
  tax: number;
  agreementTotal: number;
  // Status
  agreementStatus: AgreementStatus;
  userId: string | null;
}

// Map database agreement to frontend format
const mapAgreementToData = (agreement: ServiceAgreement): AgreementData => {
  const subtotal = agreement.subtotal || 0;
  const tax = agreement.tax || 0;

  return {
    id: agreement.id,
    createdAt: agreement.created_at,
    updatedAt: agreement.updated_at,
    leadId: agreement.lead_id,
    companyName: agreement.company_name,
    businessAddress: agreement.business_address,
    ownerName: agreement.owner_name,
    state: agreement.state,
    cityAndState: agreement.city_and_state,
    agreementDate: agreement.agreement_date,
    salesOrder: agreement.sales_order,
    customerName: agreement.customer_name,
    customerAddress: agreement.customer_address,
    customerPhone: agreement.customer_phone,
    customerEmail: agreement.customer_email,
    jobSiteAddress: agreement.job_site_address,
    jobSiteDetails: agreement.job_site_details,
    lineItems: (agreement.line_items as unknown as AgreementLineItem[]) || [],
    subtotal,
    tax,
    agreementTotal: subtotal + tax,
    agreementStatus: agreement.agreement_status,
    userId: agreement.user_id,
  };
};

// Map frontend agreement to database format
const mapDataToAgreement = (data: Partial<AgreementData>): Partial<ServiceAgreementInsert> => {
  const mapped: Partial<ServiceAgreementInsert> = {};

  if (data.leadId !== undefined) mapped.lead_id = data.leadId;
  if (data.companyName !== undefined) mapped.company_name = data.companyName;
  if (data.businessAddress !== undefined) mapped.business_address = data.businessAddress;
  if (data.ownerName !== undefined) mapped.owner_name = data.ownerName;
  if (data.state !== undefined) mapped.state = data.state;
  if (data.cityAndState !== undefined) mapped.city_and_state = data.cityAndState;
  if (data.agreementDate !== undefined) mapped.agreement_date = data.agreementDate;
  if (data.salesOrder !== undefined) mapped.sales_order = data.salesOrder;
  if (data.customerName !== undefined) mapped.customer_name = data.customerName;
  if (data.customerAddress !== undefined) mapped.customer_address = data.customerAddress;
  if (data.customerPhone !== undefined) mapped.customer_phone = data.customerPhone;
  if (data.customerEmail !== undefined) mapped.customer_email = data.customerEmail;
  if (data.jobSiteAddress !== undefined) mapped.job_site_address = data.jobSiteAddress;
  if (data.jobSiteDetails !== undefined) mapped.job_site_details = data.jobSiteDetails;
  if (data.lineItems !== undefined) mapped.line_items = data.lineItems as unknown as Json;
  if (data.subtotal !== undefined) mapped.subtotal = data.subtotal;
  if (data.tax !== undefined) mapped.tax = data.tax;
  if (data.agreementStatus !== undefined) mapped.agreement_status = data.agreementStatus;
  if (data.userId !== undefined) mapped.user_id = data.userId;

  return mapped;
};

export interface GetAgreementsParams {
  leadId?: string;
  status?: AgreementStatus;
  page?: number;
  pageSize?: number;
}

export interface GetAgreementsResponse {
  data: AgreementData[];
  count: number;
}

class AgreementsApi {
  /**
   * Get all agreements with optional filters
   */
  async getAll(params: GetAgreementsParams = {}): Promise<GetAgreementsResponse> {
    const pageSize = params.pageSize || 100;
    const page = params.page || 1;
    const offset = (page - 1) * pageSize;

    let query = supabase
      .from('service_agreements')
      .select('*', { count: 'exact' });

    // Apply filters
    if (params.leadId) {
      query = query.eq('lead_id', params.leadId);
    }

    if (params.status) {
      query = query.eq('agreement_status', params.status);
    }

    // Apply sorting (newest first)
    query = query.order('created_at', { ascending: false });

    // Apply pagination
    query = query.range(offset, offset + pageSize - 1);

    const { data, error, count } = await query;

    if (error) {
      // console.error('Failed to fetch agreements:', error);
      throw error;
    }

    return {
      data: (data || []).map(mapAgreementToData),
      count: count || 0,
    };
  }

  /**
   * Get agreements for a specific lead
   */
  async getByLead(leadId: string): Promise<AgreementData[]> {
    const { data, error } = await supabase
      .from('service_agreements')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false });

    if (error) {
      // console.error('Failed to fetch agreements by lead:', error);
      throw error;
    }

    return (data || []).map(mapAgreementToData);
  }

  /**
   * Get a single agreement by ID
   */
  async getById(id: string): Promise<AgreementData | null> {
    const { data, error } = await supabase
      .from('service_agreements')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      // console.error('Failed to fetch agreement:', error);
      throw error;
    }

    return data ? mapAgreementToData(data) : null;
  }

  /**
   * Create a new agreement
   */
  async create(agreementData: Omit<AgreementData, 'id' | 'createdAt' | 'updatedAt' | 'agreementTotal'>): Promise<AgreementData> {
    // console.log('agreementsApi.create called with:', {
    //   customerName: agreementData.customerName,
    //   leadId: agreementData.leadId,
    // });

    const dbData = mapDataToAgreement(agreementData) as ServiceAgreementInsert;

    const { data, error } = await supabase
      .from('service_agreements')
      .insert(dbData as any)
      .select()
      .single();

    if (error) {
      // console.error('Failed to create agreement:', error);
      throw new Error(`Agreement creation failed: ${error.message}`);
    }

    // console.log('Agreement created successfully:', (data as ServiceAgreement).id);
    return mapAgreementToData(data as ServiceAgreement);
  }

  /**
   * Update an existing agreement
   */
  async update(id: string, agreementData: Partial<AgreementData>): Promise<AgreementData> {
    const dbData = mapDataToAgreement(agreementData) as ServiceAgreementUpdate;

    const { data, error } = await (supabase
      .from('service_agreements')
      .update(dbData as any)
      .eq('id', id)
      .select()
      .single() as any);

    if (error) {
      // console.error('Failed to update agreement:', error);
      throw error;
    }

    return mapAgreementToData(data as ServiceAgreement);
  }

  /**
   * Delete an agreement
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('service_agreements')
      .delete()
      .eq('id', id);

    if (error) {
      // console.error('Failed to delete agreement:', error);
      throw error;
    }
  }

  /**
   * Update agreement status
   */
  async updateStatus(id: string, status: AgreementStatus): Promise<AgreementData> {
    return this.update(id, { agreementStatus: status });
  }

  /**
   * Mark agreement as sent
   */
  async markAsSent(id: string): Promise<AgreementData> {
    return this.updateStatus(id, 'sent');
  }

  /**
   * Mark agreement as signed
   */
  async markAsSigned(id: string): Promise<AgreementData> {
    return this.updateStatus(id, 'signed');
  }

  /**
   * Mark agreement as completed
   */
  async markAsCompleted(id: string): Promise<AgreementData> {
    return this.updateStatus(id, 'completed');
  }

  /**
   * Generate next sales order number
   */
  async generateSalesOrderNumber(): Promise<string> {
    const year = new Date().getFullYear();
    const prefix = `SO-${year}-`;

    try {
      const { data, error } = await supabase
        .from('service_agreements')
        .select('sales_order')
        .like('sales_order', `${prefix}%`)
        .order('sales_order', { ascending: false })
        .limit(1);

      if (error) {
        // console.error('Error fetching latest sales order:', error);
        return `${prefix}0001`;
      }

      let nextNumber = 1;
      if (data && data.length > 0) {
        const salesOrder = (data[0] as { sales_order: string | null }).sales_order;
        if (salesOrder) {
          const lastNumber = parseInt(salesOrder.split('-').pop() || '0', 10);
          nextNumber = lastNumber + 1;
        }
      }

      return `${prefix}${String(nextNumber).padStart(4, '0')}`;
    } catch (error) {
      // console.error('Error generating sales order number:', error);
      return `${prefix}0001`;
    }
  }
}

export const agreementsApi = new AgreementsApi();

// Agreement status labels
export const agreementStatusLabels: Record<AgreementStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  signed: 'Signed',
  completed: 'Completed',
};

// Agreement status colors
export const agreementStatusColors: Record<AgreementStatus, string> = {
  draft: '#6B7280',
  sent: '#3B82F6',
  signed: '#10B981',
  completed: '#8B5CF6',
};
